import {
  actionCreator,
  reducedStream,
  reducer,
  ofType,
  extractPayload,
  Action,
} from '@ardoq/rxbeach';
import { I18nMessage } from '@ardoq/i18n';
import { Subject, delay, first, map, merge, mergeMap, timer } from 'rxjs';
import { EXIT_DURATION, TOAST_DURATION } from './animationUtils';
import {
  StackedToast,
  Toast,
  Toasts,
  ToastType,
  I18nToastActionButton,
  UntranslatedActionButton,
  ToastActionButton,
} from './types';
import { logError } from '@ardoq/logging';
import { ReactElement } from 'react';
import { random } from 'lodash';

// This module keeps the toast store self-contained. It manages its own action
// and error streams internally, minimizing its public interface. Consumers show
// toasts with i18nToast and display them by rendering the ToastStack component.

const action$ = new Subject<Action<unknown>>();
const errorSubject = new Subject<any>();
errorSubject.subscribe(logError);

const MAX_OPEN_TOASTS = 3;

const toastReceived = actionCreator<Toast>('[Toast] TOAST_RECEIVED');
const toastClosed = actionCreator<number>('[Toast] TOAST_CLOSED');
const toastRemoved = actionCreator<number>('[Toast] TOAST_REMOVED');

export const closeToast = (id: number): void => action$.next(toastClosed(id));

const defaultToast = {
  id: 0,
  message: '',
  type: ToastType.INFO,
};

const initToast = (options: {
  type: ToastType;
  actionButton?: ToastActionButton;
  maxWidth?: 'fit-content';
}): Toast => ({
  ...defaultToast,
  ...options,
  id: random(1, Number.MAX_SAFE_INTEGER),
});

const dispatchToast = (toast: Toast): VoidFunction => {
  action$.next(toastReceived(toast));
  return () => closeToast(toast.id);
};

type ShowToastOptions = {
  actionButton?: UntranslatedActionButton;
  maxWidth?: 'fit-content';
};

/**
 * @deprecated Use i18nToast instead, user communication is always localized
 */
export const showToast = (
  message: string | ReactElement,
  type: ToastType = ToastType.INFO,
  options?: ShowToastOptions
): VoidFunction => {
  return dispatchToast({
    ...initToast({ type, ...options }),
    message,
  });
};

/**
 * For use cases that don't fit `i18nToast`, such as rich content (a
 * `ReactElement`) or text composed from several messages. Prefer `i18nToast`.
 */
export const showCustomToast = (
  message: string | ReactElement,
  type: ToastType = ToastType.INFO,
  options?: ShowToastOptions
): VoidFunction => {
  return dispatchToast({
    ...initToast({ type, ...options }),
    message,
  });
};

type I18nToastOptions = {
  type: ToastType;
  actionButton?: I18nToastActionButton;
  maxWidth?: 'fit-content';
};

export const i18nToast = (
  message: I18nMessage,
  { type, ...options }: I18nToastOptions
) =>
  dispatchToast({
    ...initToast({ type, ...options }),
    message,
  });

const isOpenToast = (toast: StackedToast): boolean => toast.phase === 'open';

const toClosing = (toast: StackedToast): StackedToast =>
  isOpenToast(toast) ? { ...toast, phase: 'closing' } : toast;

const closeExcessToasts = (toasts: StackedToast[]): StackedToast[] => {
  const excessIds = new Set(
    toasts
      .filter(isOpenToast)
      .slice(MAX_OPEN_TOASTS)
      .map(({ id }) => id)
  );
  return toasts.map(toast =>
    excessIds.has(toast.id) ? toClosing(toast) : toast
  );
};

const toastReceivedReducer = ({ toasts }: Toasts, toast: Toast): Toasts => ({
  toasts: closeExcessToasts([{ ...toast, phase: 'open' }, ...toasts]),
});

const toastClosedReducer = ({ toasts }: Toasts, id: number): Toasts => ({
  toasts: toasts.map(toast => (toast.id === id ? toClosing(toast) : toast)),
});

const toastRemovedReducer = ({ toasts }: Toasts, id: number): Toasts => ({
  toasts: toasts.filter(toast => toast.id !== id),
});

export const toast$ = reducedStream<Toasts>(
  'toast$',
  { toasts: [] },
  [
    reducer(toastReceived, toastReceivedReducer),
    reducer(toastClosed, toastClosedReducer),
    reducer(toastRemoved, toastRemovedReducer),
  ],
  {
    action$,
    errorSubject,
  }
);

const isClosing = ({ toasts }: Toasts, id: number): boolean =>
  toasts.some(toast => toast.id === id && !isOpenToast(toast));

const expire = (id: number) =>
  timer(TOAST_DURATION).pipe(map(() => toastClosed(id)));

const removeOnceClosed = (id: number) =>
  toast$.pipe(
    first(state => isClosing(state, id)),
    delay(EXIT_DURATION),
    map(() => toastRemoved(id))
  );

const toastLifetimes$ = action$.pipe(
  ofType(toastReceived),
  extractPayload(),
  mergeMap(({ id }) => merge(expire(id), removeOnceClosed(id)))
);

toast$.subscribe();

const subscribe = () => {
  toastLifetimes$.subscribe({
    next: action => action$.next(action),
    error(error: any) {
      errorSubject.next(error);
      subscribe();
    },
  });
};

subscribe();
